import { CreatePermissionFormData } from '@/components/permissions/CreatePermission/defines';
import { api } from '@/lib/api/api';
import { AssignmentGroupType, WeekDayNames } from '@gettactic/api';
import {
  IResource,
  IResourcePermission,
  PostResourceScheduledPermissions
} from '@gettactic/api';
import { useStandardApiHandler } from '@gettactic/helpers';
import { addDays, format } from 'date-fns';
import React from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';

export interface CreatePermissionState {
  isLoading: boolean;
  onCreated: () => void;
  onSave: (data: CreatePermissionFormData) => void;
  resources: IResource[];
  submit: (data: CreatePermissionFormData) => Promise<void>;
  form: UseFormReturn<CreatePermissionFormData>;
  group: AssignmentGroupType;
  resetToDefaults: () => void;
}

export interface CreatePermissionProviderProps
  extends React.PropsWithChildren,
    Pick<
      CreatePermissionState,
      'onCreated' | 'onSave' | 'resources' | 'group'
    > {}

export const CreatePermissionContext = React.createContext<
  CreatePermissionState | undefined
>(undefined);

function getDefaultValues(
  permission?: IResourcePermission
): CreatePermissionFormData {
  if (permission) {
    throw new Error('Edition not implemented');
  }
  return {
    users: [],
    teams: [],
    since: new Date(),
    until: addDays(new Date(), 15),
    permanentAssignment: true,
    noExpirationDate: true,
    allUsers: false,
    anchorDays: {
      sunday: false,
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false
    }
  };
}
export const defaultValues = getDefaultValues();

export const CreatePermissionProvider: React.FC<
  CreatePermissionProviderProps
> = (props) => {
  const { children, resources, onCreated, onSave, group } = props;
  const { wrapApiRequest, isLoading } = useStandardApiHandler();

  const form = useForm<CreatePermissionFormData>({
    // TODO: Replace with Zod
    // resolver: yupResolver(schemaNewPermission),
    defaultValues
  });

  const resetToDefaults = () => {
    form.reset(defaultValues);
  };
  const submit = wrapApiRequest(
    async (data: CreatePermissionFormData) => {
      const resourceIds = resources.map((x) => x.id);
      const body: PostResourceScheduledPermissions = {
        resource_ids: resourceIds,
        users: data.users,
        teams: data.teams,
        since:
          data.permanentAssignment || !data.since
            ? undefined
            : `${format(data.since, 'yyyy-MM-dd')}T00:00:00.000Z`,
        until:
          data.permanentAssignment || data.noExpirationDate || !data.until
            ? undefined
            : `${format(data.until, 'yyyy-MM-dd')}T23:59:59.000Z`,
        is_approvable: false,
        access_group_type: group,
        ...(data.anchorDays && {
          anchor_days: Object.keys(data.anchorDays).filter(
            (day) => data.anchorDays[day as WeekDayNames]
          )
        })
      };

      if ((body.teams || []).length > 0 || (body.users || []).length > 0) {
        await api.client.organizations.resourceScheduledPermissionsAdd(body);
      }

      // on created queries are invalidated and modal is closed
      // so we should clear the form
      resetToDefaults();
      onCreated();
    },
    {
      successToastMessage: 'Assignments created',
      errorToastMessage: "Sorry we couldn't save the assignments"
    }
  );

  return (
    <CreatePermissionContext.Provider
      value={{
        isLoading,
        onCreated,
        onSave,
        resources,
        submit,
        form,
        group,
        resetToDefaults
      }}
    >
      {children}
    </CreatePermissionContext.Provider>
  );
};

export const useCreatePermissionProvider = () => {
  const context = React.useContext(CreatePermissionContext);
  if (context === undefined) {
    throw new Error(
      'useCreatePermission must be used within a CreatePermissionProvider'
    );
  }
  return context;
};
